import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    Shield, ChevronDown, ChevronUp, Check, X, Loader2, ArrowRight,
    Heart, CheckCircle, FileText, Sparkles, PenLine, CheckCircle2, ClipboardList, HeartPulse,
} from "lucide-react";
import { api } from "@/lib/api";
import { ListenButton } from "@/components/VoiceControls";
import { HIPAAWaiverForm } from "@/components/HIPAAWaiverForm";
import { PHQ9Form } from "@/components/PHQ9Form";

const TREATMENT_ID = "consent_for_treatment";
const AUTHORIZATION_ID = "hipaa_authorization";

// ───────────────────────── shared bits ─────────────────────────
const StepHeader = ({ icon: Icon, eyebrow, title, subtitle }) => (
    <div className="flex items-start gap-3 mb-5">
        <span className="w-11 h-11 rounded-2xl bg-phoenix-navy text-white flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5" />
        </span>
        <div className="min-w-0">
            {eyebrow && (
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-phoenix-orange mb-0.5">{eyebrow}</div>
            )}
            <h2 className="font-display font-bold text-xl text-phoenix-navy leading-tight">{title}</h2>
            {subtitle && <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{subtitle}</p>}
        </div>
    </div>
);

// A blanket consent staff already resolved for this patient during intake —
// shown instead of asking the patient to agree to it again (phase 3 reconciliation).
const IntakeBadge = ({ info }) => {
    if (!info) return null;
    const refused = info.method === "refused";
    const label = refused ? "Refused at intake" : info.method === "verbal" ? "Verbal consent at intake" : "Signed at intake";
    const dateStr = info.captured_at ? new Date(info.captured_at).toLocaleDateString() : "";
    return (
        <div data-testid="intake-resolved-badge"
            className={`mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full border ${
                refused ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
            {refused ? <X className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
            {label}{dateStr ? ` · ${dateStr}` : ""}
            {refused && info.refusal_reason ? ` — ${info.refusal_reason}` : ""}
        </div>
    );
};

const ConsentRow = ({ item, idx, checked, expanded, onToggleCheck, onToggleExpand }) => {
    const resolved = item.intake_resolved;
    return (
    <div
        data-testid={`consent-item-${item.id}`}
        className={`rounded-2xl border transition-all ${checked || resolved ? "border-phoenix-navy/40 bg-phoenix-soft/40" : "border-border bg-white"}`}
    >
        <div className="flex items-start gap-3 p-4">
            {resolved ? (
                <span className={`w-6 h-6 rounded-md shrink-0 mt-0.5 flex items-center justify-center ${
                    resolved.method === "refused" ? "bg-red-500" : "bg-emerald-500"}`}>
                    {resolved.method === "refused" ? <X className="w-4 h-4 text-white" /> : <Check className="w-4 h-4 text-white" />}
                </span>
            ) : (
                <button
                    type="button"
                    onClick={onToggleCheck}
                    data-testid={`consent-checkbox-${item.id}`}
                    aria-pressed={checked}
                    className={`w-6 h-6 rounded-md border-2 shrink-0 mt-0.5 flex items-center justify-center transition-all ${
                        checked ? "bg-phoenix-navy border-phoenix-navy" : "border-muted-foreground/50 hover:border-phoenix-navy"}`}
                >
                    {checked && <Check className="w-4 h-4 text-white" />}
                </button>
            )}
            <div className="flex-1 min-w-0">
                <button type="button" onClick={resolved ? onToggleExpand : onToggleCheck}
                    className="text-sm font-display font-semibold text-phoenix-navy text-left leading-snug">
                    {idx != null ? `${idx}. ` : ""}{item.label}
                </button>
                {resolved && <IntakeBadge info={resolved} />}
                <button type="button" onClick={onToggleExpand}
                    data-testid={`consent-details-${item.id}`}
                    className="flex items-center gap-1 text-xs text-phoenix-navy/80 hover:text-phoenix-navy mt-1.5 transition-colors">
                    {expanded ? (<><ChevronUp className="w-3 h-3" /> Hide details</>) : (<><ChevronDown className="w-3 h-3" /> View details</>)}
                </button>
                {expanded && (
                    <>
                        <div className="mt-2.5"><ListenButton text={`${item.label}. ${item.detail || ""}`} label="Listen" /></div>
                        <p className="text-xs text-muted-foreground mt-2.5 leading-relaxed whitespace-pre-wrap max-h-72 overflow-y-auto pr-1">
                            {item.detail}
                        </p>
                    </>
                )}
            </div>
        </div>
    </div>
    );
};

const PrimaryBtn = ({ children, disabled, onClick, testid }) => (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
        className="w-full py-3.5 bg-phoenix-navy text-white font-semibold rounded-2xl hover:bg-phoenix-navy-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2">
        {children}
    </button>
);

// ───────────────────────── step renderers ─────────────────────────
const WelcomeStep = ({ firstName, onNext }) => {
    const [saving, setSaving] = useState(false);
    const go = async () => {
        setSaving(true);
        try { await api.post("/onboarding/welcome-seen"); onNext(); }
        finally { setSaving(false); }
    };
    const FEATURES = [
        { icon: Heart, title: "Daily Check-In", desc: "Log your mood, journal, and gratitude" },
        { icon: CheckCircle, title: "Skills & Tools", desc: "Learn CBT/DBT techniques and coping strategies" },
        { icon: FileText, title: "Reentry Prep", desc: "Build a restoration plan and work the reentry checklist" },
    ];
    return (
        <div data-testid="onboarding-welcome" className="space-y-6">
            <div className="text-center">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-phoenix-orange mb-2">Welcome to Blue Phoenix</div>
                <h2 className="font-display font-bold text-2xl sm:text-3xl text-phoenix-navy">Hi {firstName}.</h2>
                <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto leading-relaxed">
                    Let's get you set up. The next few steps cover your privacy, your rights, and a quick check-in. It takes about 5 minutes and only happens once.
                </p>
            </div>
            <div className="space-y-2">
                {FEATURES.map((f) => (
                    <div key={f.title} className="flex items-start gap-3 p-3 rounded-xl bg-secondary/40 border border-border/60">
                        <div className="w-9 h-9 rounded-xl bg-phoenix-navy/10 flex items-center justify-center shrink-0">
                            <f.icon className="w-4 h-4 text-phoenix-navy" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-semibold text-sm text-phoenix-navy">{f.title}</div>
                            <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{f.desc}</div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 flex items-start gap-3">
                <Sparkles className="w-4 h-4 text-phoenix-orange mt-0.5 shrink-0" />
                <div className="text-sm text-phoenix-navy leading-relaxed">
                    <strong>Your space, your pace.</strong> Everything you write is private — only reviewed if a safety concern is flagged.
                </div>
            </div>
            <PrimaryBtn onClick={go} disabled={saving} testid="onboarding-welcome-continue">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Get started <ArrowRight className="w-4 h-4" /></>}
            </PrimaryBtn>
        </div>
    );
};

const AppConsentsStep = ({ items, checked, setChecked, expanded, setExpanded, onNext }) => {
    const isSettled = (i) => !!checked[i.id] || !!i.intake_resolved;
    const allChecked = items.length > 0 && items.every(isSettled);
    const remaining = items.filter((i) => !isSettled(i)).length;
    return (
        <div data-testid="consent-modal" className="space-y-4">
            <StepHeader icon={Shield} eyebrow="App agreements" title="Your privacy & app use"
                subtitle="Blue Phoenix handles sensitive information. Review and agree to each item — tap to read the full text." />
            <div className="space-y-2.5">
                {items.map((item, i) => (
                    <ConsentRow key={item.id} item={item} idx={i + 1} checked={isSettled(item)}
                        expanded={expanded === item.id}
                        onToggleCheck={() => setChecked((p) => ({ ...p, [item.id]: !p[item.id] }))}
                        onToggleExpand={() => setExpanded(expanded === item.id ? null : item.id)} />
                ))}
            </div>
            <div className="flex flex-wrap gap-3 text-xs pt-1">
                <Link to="/privacy" target="_blank" rel="noreferrer" className="text-phoenix-navy hover:underline">Privacy Policy</Link>
                <span className="text-muted-foreground">·</span>
                <Link to="/terms" target="_blank" rel="noreferrer" className="text-phoenix-navy hover:underline">Terms of Service</Link>
            </div>
            <PrimaryBtn onClick={onNext} disabled={!allChecked} testid="app-consents-continue">
                {allChecked ? <>I agree — Continue <ArrowRight className="w-4 h-4" /></> : `Check all ${items.length} items to continue (${remaining} left)`}
            </PrimaryBtn>
        </div>
    );
};

const SingleConsentStep = ({ item, eyebrow, title, subtitle, checked, setChecked, onNext, testid, continueTestid }) => {
    if (!item) { onNext(); return null; }
    const resolved = item.intake_resolved;
    return (
        <div data-testid={testid} className="space-y-4">
            <StepHeader icon={FileText} eyebrow={eyebrow} title={title} subtitle={subtitle} />
            <div className="bg-white border border-border/60 rounded-2xl p-4 text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap max-h-[42vh] overflow-y-auto">
                {item.detail}
            </div>
            {resolved ? (
                <IntakeBadge info={resolved} />
            ) : (
                <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                    <input data-testid={`consent-checkbox-${item.id}`} type="checkbox" className="rounded mt-0.5"
                        checked={!!checked[item.id]} onChange={(e) => setChecked((p) => ({ ...p, [item.id]: e.target.checked }))} />
                    <span className="text-muted-foreground">I have read and agree to the <strong className="text-phoenix-navy">{item.label}</strong> above.</span>
                </label>
            )}
            <PrimaryBtn onClick={onNext} disabled={!resolved && !checked[item.id]} testid={continueTestid}>
                Continue <ArrowRight className="w-4 h-4" />
            </PrimaryBtn>
        </div>
    );
};

const AuthorizationStep = ({ item, allTemplateItems, checked, setChecked, onSubmitConsent }) => {
    const [signature, setSignature] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const resolved = item?.intake_resolved;
    const ready = !!item && (resolved || !!checked[item.id]) && signature.trim().length >= 2;

    const submit = async () => {
        setError(""); setSaving(true);
        try {
            await api.post("/onboarding/consent/accept", {
                items: allTemplateItems.map((i) => ({ id: i.id, label: i.label, agreed: !!checked[i.id] })),
                signature: signature.trim(),
                user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
            });
            onSubmitConsent();
        } catch (e) {
            setError(e?.response?.data?.detail
                ? (Array.isArray(e.response.data.detail) ? "Please complete all required agreements." : e.response.data.detail)
                : "Could not save your authorization. Please try again.");
            setSaving(false);
        }
    };

    if (!item) { onSubmitConsent(); return null; }
    return (
        <div data-testid="authorization-step" className="space-y-4">
            <StepHeader icon={Shield} eyebrow="Care team authorization"
                title="HIPAA Authorization (Release of Information)"
                subtitle="This authorizes your internal Blue Phoenix care team to use and share your health information to coordinate your care." />
            <div className="bg-white border border-border/60 rounded-2xl p-4 text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap max-h-[38vh] overflow-y-auto">
                {item.detail}
            </div>
            {resolved ? (
                <IntakeBadge info={resolved} />
            ) : (
                <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                    <input data-testid={`consent-checkbox-${item.id}`} type="checkbox" className="rounded mt-0.5"
                        checked={!!checked[item.id]} onChange={(e) => setChecked((p) => ({ ...p, [item.id]: e.target.checked }))} />
                    <span className="text-muted-foreground">I have read and authorize the use and disclosure of my health information as described above.</span>
                </label>
            )}
            <div>
                <label className="block text-xs font-semibold text-phoenix-navy uppercase tracking-wider mb-1.5">Type your full legal name to sign</label>
                <input data-testid="authorization-signature-input" type="text"
                    className="w-full px-3.5 py-2.5 bg-white border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30"
                    placeholder="Your full name" value={signature} onChange={(e) => setSignature(e.target.value)} />
            </div>
            {error && (
                <div data-testid="consent-error" className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded-xl px-3 py-2">{error}</div>
            )}
            <PrimaryBtn onClick={submit} disabled={!ready || saving} testid="consent-accept">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PenLine className="w-4 h-4" /> Sign & Continue</>}
            </PrimaryBtn>
        </div>
    );
};

const PatientRightsStep = ({ onNext }) => {
    const [tpl, setTpl] = useState(null);
    const [ack, setAck] = useState(false);
    const [signature, setSignature] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/onboarding/patient-rights").then((r) => setTpl(r.data)).catch(() => setTpl({ items: [] }));
    }, []);

    const ready = ack && signature.trim().length >= 2;
    const submit = async () => {
        setError(""); setSaving(true);
        try {
            await api.post("/onboarding/patient-rights/acknowledge", {
                acknowledged: true, signature: signature.trim(),
                user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
            });
            onNext();
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not save your acknowledgment. Please try again.");
            setSaving(false);
        }
    };

    return (
        <div data-testid="patient-rights-step" className="space-y-4">
            <StepHeader icon={ClipboardList} eyebrow="Know your rights" title="Your Rights in Correctional Health Care"
                subtitle="Please review your health-care rights. This is an acknowledgment that you were informed — it is not a consent to any treatment." />
            <div className="bg-white border border-border/60 rounded-2xl divide-y divide-border/60 max-h-[44vh] overflow-y-auto">
                {(tpl?.items || []).map((it) => (
                    <div key={it.id} className="p-4">
                        <div className="text-sm font-display font-semibold text-phoenix-navy">{it.label}</div>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{it.detail}</p>
                    </div>
                ))}
            </div>
            <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                <input data-testid="patient-rights-ack" type="checkbox" className="rounded mt-0.5"
                    checked={ack} onChange={(e) => setAck(e.target.checked)} />
                <span className="text-muted-foreground">I acknowledge that I have been informed of my rights in correctional health care.</span>
            </label>
            <div>
                <label className="block text-xs font-semibold text-phoenix-navy uppercase tracking-wider mb-1.5">Type your full legal name to sign</label>
                <input data-testid="patient-rights-signature" type="text"
                    className="w-full px-3.5 py-2.5 bg-white border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30"
                    placeholder="Your full name" value={signature} onChange={(e) => setSignature(e.target.value)} />
            </div>
            {error && <div data-testid="patient-rights-error" className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded-xl px-3 py-2">{error}</div>}
            <PrimaryBtn onClick={submit} disabled={!ready || saving} testid="patient-rights-continue">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PenLine className="w-4 h-4" /> Acknowledge & Continue</>}
            </PrimaryBtn>
        </div>
    );
};

const SuccessStep = ({ onComplete }) => {
    const [saving, setSaving] = useState(false);
    const go = () => { setSaving(true); onComplete(); };
    return (
        <div data-testid="onboarding-success" className="text-center py-6 space-y-5">
            <div className="w-16 h-16 rounded-full bg-success/15 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-9 h-9 text-success" />
            </div>
            <div>
                <h2 className="font-display font-bold text-2xl text-phoenix-navy">You're all set.</h2>
                <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto leading-relaxed">
                    Thank you. Your agreements are saved and your check-in is complete. You can review your consents and manage your medical record release anytime from the menu in the top right.
                </p>
            </div>
            <PrimaryBtn onClick={go} disabled={saving} testid="onboarding-success-enter">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Enter Blue Phoenix <ArrowRight className="w-4 h-4" /></>}
            </PrimaryBtn>
        </div>
    );
};

// ───────────────────────── orchestrator ─────────────────────────
export default function InmateOnboardingWizard({ firstName = "Friend", user, onComplete }) {
    const [template, setTemplate] = useState(null);
    const [assessStatus, setAssessStatus] = useState(null);
    const [checked, setChecked] = useState({});
    const [expanded, setExpanded] = useState(null);
    const [stepIdx, setStepIdx] = useState(0);
    const bodyRef = useRef(null);

    useEffect(() => {
        api.get("/onboarding/consent/template").then((r) => setTemplate(r.data?.items || [])).catch(() => setTemplate([]));
        api.get("/inmate/assessments/status")
            .then((r) => setAssessStatus(r.data))
            .catch(() => setAssessStatus({ self_assessment_done: true, phq9_baseline_done: true }));
    }, []);

    const treatmentItem = useMemo(() => template?.find((i) => i.id === TREATMENT_ID) || null, [template]);
    const authItem = useMemo(() => template?.find((i) => i.id === AUTHORIZATION_ID) || null, [template]);
    const appItems = useMemo(
        () => (template || []).filter((i) => i.id !== TREATMENT_ID && i.id !== AUTHORIZATION_ID),
        [template],
    );

    // Build the ordered, de-duplicated step list once both fetches resolve.
    const steps = useMemo(() => {
        if (template == null || assessStatus == null || !user) return null;
        const consentDone = !!user.consent_accepted;
        const list = [];
        if (!user.has_seen_welcome) list.push("welcome");
        if (!consentDone) list.push("appConsents");
        if (!user.hipaa_acknowledged) list.push("hipaaPolicy");
        if (!consentDone) { list.push("treatment"); list.push("authorization"); }
        if (!user.patient_rights_acknowledged) list.push("patientRights");
        if (!assessStatus.phq9_baseline_done) list.push("phq9");
        list.push("success");
        return list;
    }, [template, assessStatus, user]);

    useEffect(() => { bodyRef.current?.scrollTo({ top: 0 }); }, [stepIdx]);

    if (steps == null) {
        return (
            <div data-testid="onboarding-wizard" className="fixed inset-0 z-[70] bg-phoenix-navy/45 backdrop-blur-sm flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-white" />
            </div>
        );
    }

    const current = steps[Math.min(stepIdx, steps.length - 1)];
    const total = steps.length;
    const isSuccess = current === "success";
    const goNext = () => setStepIdx((i) => Math.min(i + 1, steps.length - 1));

    const submitPhq9 = async (payload) => {
        try { await api.post("/inmate/assessments/phq9", payload); goNext(); } catch { /* stay */ }
    };

    let content = null;
    if (current === "welcome") {
        content = <WelcomeStep firstName={firstName} onNext={goNext} />;
    } else if (current === "appConsents") {
        content = <AppConsentsStep items={appItems} checked={checked} setChecked={setChecked}
            expanded={expanded} setExpanded={setExpanded} onNext={goNext} />;
    } else if (current === "hipaaPolicy") {
        content = (
            <div className="space-y-1">
                <StepHeader icon={Shield} eyebrow="Privacy policy" title="HIPAA Privacy Acknowledgment"
                    subtitle="Your privacy rights — and the limits of confidentiality in a correctional setting." />
                <HIPAAWaiverForm onComplete={goNext} />
            </div>
        );
    } else if (current === "treatment") {
        content = <SingleConsentStep item={treatmentItem} eyebrow="Treatment"
            title="Consent for Treatment" subtitle="Behavioral health, psychiatric, and medical care — including telehealth (video) appointments and infectious disease screening."
            checked={checked} setChecked={setChecked} onNext={goNext}
            testid="treatment-consent-step" continueTestid="treatment-continue" />;
    } else if (current === "authorization") {
        content = <AuthorizationStep item={authItem} allTemplateItems={template} checked={checked}
            setChecked={setChecked} onSubmitConsent={goNext} />;
    } else if (current === "patientRights") {
        content = <PatientRightsStep onNext={goNext} />;
    } else if (current === "phq9") {
        content = (
            <div className="space-y-3">
                <StepHeader icon={HeartPulse} eyebrow="Wellness check-in" title="How you've been feeling (PHQ-9)"
                    subtitle="A standard 9-question check-in. There are no wrong answers." />
                <PHQ9Form onSubmit={submitPhq9} submitLabel="Finish check-in" />
            </div>
        );
    } else if (current === "success") {
        content = <SuccessStep onComplete={onComplete} />;
    }

    return (
        <div data-testid="onboarding-wizard"
            className="fixed inset-0 z-[70] bg-phoenix-navy/45 backdrop-blur-sm overflow-y-auto">
            <div className="min-h-full flex items-start sm:items-center justify-center p-3 sm:p-6">
                <div className="w-full max-w-xl bg-white rounded-3xl shadow-phoenix-float my-4 overflow-hidden flex flex-col max-h-[94vh] animate-fade-up">
                    {/* Progress chrome */}
                    {!isSuccess && (
                        <div className="px-6 pt-5 pb-4 border-b border-border/60 shrink-0">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-[0.18em]">Account setup</span>
                                <span data-testid="wizard-progress" className="text-xs font-semibold text-phoenix-navy">
                                    Step {Math.min(stepIdx + 1, total - 1)} of {total - 1}
                                </span>
                            </div>
                            <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                                <div className="h-full bg-phoenix-navy rounded-full transition-all duration-500"
                                    style={{ width: `${(stepIdx / Math.max(total - 1, 1)) * 100}%` }} />
                            </div>
                        </div>
                    )}
                    <div ref={bodyRef} className="flex-1 overflow-y-auto p-6 sm:p-7">
                        {content}
                    </div>
                </div>
            </div>
        </div>
    );
}
